import React, { useState, useRef, useMemo } from 'react';
import moment from 'moment';
import 'moment-duration-format';
import './DriverScheduleTable.scss';

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

const TimelineBar = ({ servicesConcatenated, estatus, selectedFilterType, consultedDate }) => {
    const totalMinutesInDay = 24 * 60;
    const timelineRef = useRef(null);
    const [hoverTime, setHoverTime] = useState('');


    // Definir los colores para cada estatus de servicio del punto
    const serviceStatusDotColors = useMemo(() => ({
        'TERMINADA': '#62e2f8ff',
        'EN CURSO': '#00FF00',
        'CONFIRMADA': '#FFC107',
        'PROGRAMADA': '#ffffffff'
    }), []);

    // Definir los colores de fondo para los tipos de servicio de la BARRA DE SERVICIO Y el FONDO HORARIO
    const serviceTypeColors = useMemo(() => ({
        'ADMINISTRATIVO': '#9C27B0', // Morado
        'TALLER': '#4CAF50',         // Verde
        'ESPECIAL': '#FFCC99',       // Beige/Amarillo claro
        'EMPRESARIAL': '#2E89E4'     // Azul empresarial (Cian)
    }), []);

    const parseServices = (servicesString) => {
        if (!servicesString || servicesString === 'Sin Servicios') {
            return [];
        }

        const serviceEntries = servicesString.split(',');

        let parsed = serviceEntries.map(entry => {
            const parts = entry.split('|');
            console.log("parts", parts)
            // ID|NOMBRE_RUTA|KmTrazo : VALOR|KmRecor : VALOR|ESTATUS|TIPO_SERVICIO|HORA_ARRANQUE|HORA_LLEGADA
            if (parts.length < 8) {
                console.warn(`Skipping malformed service entry: '${entry}'. Expected 8 parts separated by '|'. Found ${parts.length}.`);
                return null;
            }

            const id = parts[0];
            const routeName = parts[1].trim();
            const kmTrazo = parts[2].trim();
            const kmRecorrido = parts[3].trim();
            const status = parts[4].trim();
            const serviceType = parts[5].trim();
            const startTimeStr = parts[6].trim();
            const endTimeStr = parts[7].trim();

            const dateFormat = 'DD/MM/YYYY HH:mm';
            let startTime = moment(startTimeStr, dateFormat);
            let endTime = moment(endTimeStr, dateFormat);

            if (!startTime.isValid() || !endTime.isValid()) {
                console.warn(`Skipping service ${id} due to invalid date parsing. Input Start: '${startTimeStr}', Input End: '${endTimeStr}'. Parsed Start (formatted): ${startTime.format()}, Parsed End (formatted): ${endTime.format()}.`);
                return null;
            }

            // Validar que endTime no sea anterior a startTime
            if (endTime.isBefore(startTime)) {
                // Intentar corregir: usar el mismo mes/año que startTime pero mantener día y hora de endTime
                const correctedEndTime = startTime.clone()
                    .date(endTime.date())
                    .hour(endTime.hour())
                    .minute(endTime.minute());
                
                // Si la hora de fin es menor que la de inicio en el mismo día, es probable que cruce medianoche
                if (correctedEndTime.isSameOrBefore(startTime)) {
                    correctedEndTime.add(1, 'day');
                }

                console.warn(`Auto-correcting service ${id}. Original End: '${endTimeStr}' -> Corrected End: '${correctedEndTime.format('DD/MM/YYYY HH:mm')}'. Using start date as reference.`);
                endTime = correctedEndTime;
            }

            // Obtener la fecha consultada, si no está disponible usar la fecha de inicio del servicio
            // params.fecha viene en formato YYYY-MM-DD (de formateoBasico)
            const consultedDateMoment = consultedDate 
                ? moment(consultedDate, ['YYYY-MM-DD', 'DD/MM/YYYY'], true).isValid()
                    ? moment(consultedDate, ['YYYY-MM-DD', 'DD/MM/YYYY'])
                    : startTime.clone().startOf('day')
                : startTime.clone().startOf('day');

            // Verificar si la fecha consultada está dentro del rango del servicio
            const consultedDayStart = consultedDateMoment.clone().startOf('day');
            const consultedDayEnd = consultedDateMoment.clone().endOf('day');
            const serviceDayStart = startTime.clone().startOf('day');
            const serviceDayEnd = endTime.clone().startOf('day');

            // Si la fecha consultada no está en el rango del servicio, no mostrar este servicio
            if (consultedDateMoment.isBefore(serviceDayStart, 'day') || consultedDateMoment.isAfter(serviceDayEnd, 'day')) {
                console.log(`Service ${id} not on consulted date ${consultedDate}. Service range: ${startTime.format('DD/MM/YYYY')} - ${endTime.format('DD/MM/YYYY')}`);
                return null;
            }

            // Ajustar las horas según si es el primer día, un día intermedio o el último día
            let adjustedStartTime, adjustedEndTime;

            const isFirstDay = consultedDateMoment.isSame(serviceDayStart, 'day');
            const isLastDay = consultedDateMoment.isSame(serviceDayEnd, 'day');

            if (isFirstDay && isLastDay) {
                // El servicio comienza y termina el mismo día consultado
                adjustedStartTime = startTime.clone();
                adjustedEndTime = endTime.clone();
            } else if (isFirstDay) {
                // Es el primer día del servicio: desde la hora de inicio hasta 23:59
                adjustedStartTime = startTime.clone();
                adjustedEndTime = consultedDateMoment.clone().endOf('day');
            } else if (isLastDay) {
                // Es el último día del servicio: desde 00:00 hasta la hora de fin
                adjustedStartTime = consultedDateMoment.clone().startOf('day');
                adjustedEndTime = endTime.clone();
            } else {
                // Es un día intermedio: de 00:00 a 23:59
                adjustedStartTime = consultedDateMoment.clone().startOf('day');
                adjustedEndTime = consultedDateMoment.clone().endOf('day');
            }

            const startOfDay = consultedDateMoment.clone().startOf('day');

            const startMinutes = moment.duration(adjustedStartTime.diff(startOfDay)).asMinutes();
            const endMinutes = moment.duration(adjustedEndTime.diff(startOfDay)).asMinutes();

            if (isNaN(startMinutes) || isNaN(endMinutes)) {
                console.warn(`Skipping service ${id} due to NaN minutes after diff calculation. StartDiff: ${startMinutes}, EndDiff: ${endMinutes}`);
                return null;
            }

            if (endMinutes <= startMinutes) {
                console.warn(`Skipping service ${id} due to zero or negative duration. Start: ${startMinutes}, End: ${endMinutes}`);
                return null;
            }

            return {
                id: id,
                start: startMinutes,
                end: endMinutes,
                originalStart: startTime,
                originalEnd: endTime,
                isOverlap: false,
                overlappedServices: [],
                status: status,
                serviceType: serviceType,
                routeName: routeName,
                kmTrazo: kmTrazo,
                kmRecorrido: kmRecorrido,
                estatus: estatus
            };
        }).filter(Boolean);
        console.log("parsed", parsed);
        parsed.sort((a, b) => a.start - b.start);

        // Detectar solapamientos entre todos los servicios
        for (let i = 0; i < parsed.length; i++) {
            for (let j = i + 1; j < parsed.length; j++) {
                const serviceA = parsed[i];
                const serviceB = parsed[j];

                // Verificar si hay solapamiento
                if (serviceA.end > serviceB.start && serviceA.start < serviceB.end) {
                    parsed[i].isOverlap = true;
                    parsed[j].isOverlap = true;
                    parsed[i].overlappedServices.push(serviceB.id);
                    parsed[j].overlappedServices.push(serviceA.id);
                }
            }
        }


        return parsed;
    };

    const services = parseServices(servicesConcatenated);

    const renderHourlyBackgrounds = () => {
        const backgrounds = [];
        const hourTypes = new Array(24).fill(null);

        services.forEach(service => {
            const startHour = Math.floor(service.start / 60);
            const endHour = Math.ceil(service.end / 60);

            for (let h = startHour; h < endHour; h++) {
                if (h >= 0 && h < 24) {
                    hourTypes[h] = service.serviceType;
                }
            }
        });

        for (let i = 0; i < 24; i++) {
            const leftPosition = (i * 60 / totalMinutesInDay) * 100;
            const hourServiceType = hourTypes[i];
            // Usa serviceTypeColors para el fondo horario
            const backgroundColor = serviceTypeColors[hourServiceType] || 'transparent';

            backgrounds.push(
                <div
                    key={`bg-hour-${i}`}
                    className="timeline-hour-background"
                    style={{
                        left: `${leftPosition}%`,
                        width: `${(60 / totalMinutesInDay) * 100}%`,
                        // backgroundColor: backgroundColor,
                        opacity: 0.1 // Ligeramente más transparente para el fondo
                    }}
                ></div>
            );
        }
        return backgrounds;
    };

    const renderTimeTicks = () => {
        const ticks = [];
        for (let i = 0; i < 24; i += 1) {
            const leftPosition = (i * 60 / totalMinutesInDay) * 100;
            ticks.push(
                <div
                    key={`tick-hour-${i}`}
                    className="timeline-tick"
                    style={{ left: `${leftPosition}%` }}
                >
                    <span className="tick-label">{moment().startOf('day').add(i, 'hours').format('HH:mm')}</span>
                </div>
            );
        }
        return ticks;
    };

    const handleMouseMove = (e) => {
        if (timelineRef.current) {
            const timelineWidth = timelineRef.current.offsetWidth;
            const mouseX = e.nativeEvent.offsetX;

            const percentage = mouseX / timelineWidth;
            const minutes = Math.round(percentage * totalMinutesInDay);

            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;

            const formattedTime = moment().startOf('day')
                .add(hours, 'hours')
                .add(remainingMinutes, 'minutes')
                .format('HH:mm');
            setHoverTime(formattedTime);
        }
    };

    const handleMouseLeave = () => {
        setHoverTime('');
    };


    return (
        <div
            ref={timelineRef}
            className="timeline-container"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {renderHourlyBackgrounds()}
            {renderTimeTicks()}

            {services.length === 0 && <div style={{ textAlign: 'center', paddingTop: '5px', color: '#888' }}></div>}
            {services.map(service => {
                const left = (service.start / totalMinutesInDay) * 100;
                const width = ((service.end - service.start) / totalMinutesInDay) * 100;

                const finalWidth = Math.max(0, width);
                const finalLeft = Math.max(0, left);

                const dotColor = serviceStatusDotColors[service.status] || '#ccc';
                // Obtener el color de fondo de la BARRA DE SERVICIO basado en el tipo de servicio
                const barBackgroundColor = serviceTypeColors[service.serviceType] || '#4CAF50'; // Fallback a verde si el tipo no está mapeado


                return (
                    <Tooltip
                        key={`tooltip-${service.id}`}
                        title={
                            <React.Fragment>
                                <Typography color="inherit">ID: {service.id}</Typography>
                                <Typography variant="caption" display="block">
                                    Ruta: {service.routeName}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Estatus: {service.status}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Tipo: {service.serviceType}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    {service.kmTrazo}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    {service.kmRecorrido}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Inicio: {service.originalStart.format('DD/MM HH:mm')}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Fin: {service.originalEnd.format('DD/MM HH:mm')}
                                </Typography>
                                {service.isOverlap && (
                                    <>
                                        <Typography variant="caption" display="block" color="yellow">
                                            CONFLICTO DE PROGRAMACIÓN
                                        </Typography>
                                        <Typography variant="caption" display="block" color="yellow">
                                            Bitácoras : {[service.id, ...service.overlappedServices].join(', ')}
                                        </Typography>
                                    </>
                                )}
                            </React.Fragment>
                        }
                        placement="top"
                        arrow
                    >
                        <div
                            key={service.id}
                            className={`service-block ${service.isOverlap ? 'overlap-service' : ''}`} // Quitamos 'normal-service' si no se usa un color por defecto global
                            style={{
                                left: `${finalLeft}%`,
                                width: `${finalWidth}%`,
                                color: barBackgroundColor === "#FFCC99" ? "black" : "white",
                                // backgroundColor: barBackgroundColor, // <-- APLICAR EL COLOR DE FONDO DE LA BARRA AQUÍ
                                backgroundColor: selectedFilterType === 'operador' ? service.estatus !== 'Disponible' ? '#ff2222' : barBackgroundColor : barBackgroundColor, // <-- APLICAR EL COLOR DE FONDO DE LA BARRA AQUÍ
                                border: 'none' // <-- QUITAR CUALQUIER BORDE FIJO
                            }}
                        >
                            {finalWidth > 5 && (
                                <span style={{ marginRight: '10px' }}>{service.id}</span>
                            )}
                            <div className="status-dot" style={{ backgroundColor: dotColor }}></div>
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );
};

export default TimelineBar;